import D from "@/admin/dashboard/Dashboard.module.css";

import { monthlyArpaQueryAtom } from "@/admin/accounts/accountsQuery.ts";
import { CustomRadios, type FilterItem } from "@/admin/dashboard/CustomRadios.tsx";
import { HeadingBlock } from "@/admin/dashboard/HeadingBlock.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { PercentageChange } from "@/admin/dashboard/PercentageChange.tsx";
import { abbreviateNumber } from "@/admin/dashboard/utils.ts";
import { monthNames, WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import { useTooltipPortal } from "@/admin/utils/useTooltipPortal.tsx";
import { BadgeDot } from "@/components/Badge.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useConfig } from "@/config.ts";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { MonthlyArpa } from "@warrenio/api-spec/spec.oats.gen";
import type { BarSeriesOption } from "echarts/charts";
import { useState } from "react";
import { Label } from "react-aria-components";

type FilterBy = "paying_ba_count" | "arpa";

const filterItems: FilterItem<FilterBy>[] = [
    { key: "paying_ba_count", title: "Paying BA" },
    { key: "arpa", title: "ARPA" },
];

function CustomersSectionContent() {
    const limitMonths = 12;
    const { allowDebt } = useConfig();

    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - 1);
    const afterDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;

    const items = useSuspenseQueryAtom(monthlyArpaQueryAtom(afterDate, allowDebt));
    const [filterBy, setFilterBy] = useState<FilterBy>("paying_ba_count");

    const colorWhite = useThemeVar("color-white");
    const colorMuted = useThemeVar("color-muted");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");
    const colorPrimary = useThemeVar("color-primary");
    const colorPrimaryDark = useThemeVar("color-primary-dark");

    const xAxisData: string[] = [];

    for (let iteratorMonths = limitMonths; iteratorMonths > 0; iteratorMonths--) {
        const currentDate = new Date();
        const generatedDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - (iteratorMonths - 1), 1);
        const month = monthNames[generatedDate.getMonth()];
        const year = generatedDate.getFullYear();
        xAxisData.push(`${month} ${year}`);
    }

    const customersArpa: MonthlyArpa[] = [];
    let payingTotalLastMonth = 0;
    let payingTotalPrevMonth = 0;
    let arpaTotalLastMonth = 0;
    let arpaTotalPrevMonth = 0;
    const indexPrevARPA: number = allowDebt ? 2 : 3;
    const indexLastARPA: number = allowDebt ? 1 : 2;

    // biome-ignore lint/complexity/noForEach: XXX: Bad code! Should use `useMemo` or `.map()` instead of looping in render function.
    xAxisData.forEach((item) => {
        // Parse the "Month Year" string properly
        const [month, year] = item.split(" ");
        const monthIndex = monthNames.indexOf(month);
        const date = new Date(Number(year), monthIndex, 1); // Use 1st day of month

        const time = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()).valueOf() / 1000;
        const data = items.find((item) => item.period_start_month === time);
        customersArpa.push(data ?? { period_start_month: time, paying_ba_count: 0, arpa: 0 });
    });

    items.forEach((item, index) => {
        if (index + indexPrevARPA === items.length) {
            payingTotalPrevMonth = item.paying_ba_count;
            arpaTotalPrevMonth = item.arpa;
        }
        if (index + indexLastARPA === items.length) {
            payingTotalLastMonth = item.paying_ba_count;
            arpaTotalLastMonth = item.arpa;
        }
    });

    const { portal, formatter } = useTooltipPortal(({ name, dataIndex }) => {
        return (
            <div>
                <div>
                    <b>{name}</b>
                </div>

                <BadgeDot color="color-primary">
                    Paying BA: <b>{customersArpa[dataIndex].paying_ba_count}</b>
                </BadgeDot>

                <BadgeDot color="color-primary-dark">
                    ARPA:{" "}
                    <b>
                        <CurrencyBalance value={customersArpa[dataIndex].arpa} />
                    </b>
                </BadgeDot>
            </div>
        );
    });

    const seriesData: BarSeriesOption[] = [
        {
            name: filterBy,
            type: "bar",
            stack: "total",
            itemStyle: {
                color: filterBy === "paying_ba_count" ? colorPrimary : colorPrimaryDark,
            },
            tooltip: { formatter },
            data: customersArpa.map((item) => item[filterBy]),
        },
    ];

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "5px",
            right: "5px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xAxisData,
            axisLine: {
                lineStyle: {
                    color: colorMuted,
                },
            },
            axisTick: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: seriesData,
    };

    return (
        <>
            {portal}

            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Customers</h2>
                    <p>Paying billing accounts (BA) and average revenue per billing account (ARPA)</p>
                </div>

                <HeadingBlock title="Paying BAs last month" subtitle={payingTotalLastMonth}>
                    <PercentageChange last={payingTotalLastMonth} prev={payingTotalPrevMonth} />
                </HeadingBlock>

                <HeadingBlock title="ARPA last month" subtitle={<CurrencyBalance value={arpaTotalLastMonth} />}>
                    <PercentageChange last={arpaTotalLastMonth} prev={arpaTotalPrevMonth} />
                </HeadingBlock>
            </div>

            <Separator />

            <div className="flex justify-between pt-3 pb-3">
                <div className="flex gap-1rem font-size-small">
                    <BadgeDot color={filterBy === "paying_ba_count" ? "color-primary" : "color-primary-dark"}>
                        {filterBy === "paying_ba_count" ? "Paying BA" : "ARPA"}
                    </BadgeDot>
                </div>

                <div className="flex items-center">
                    <Label className="font-size-small pr-1">Filter by:</Label>
                    <div className="flex items-center">
                        <CustomRadios items={filterItems} value={filterBy} onChange={setFilterBy} />
                    </div>
                </div>
            </div>

            <WBarChart options={options} />
        </>
    );
}

export function CustomersSection() {
    return (
        <LightBlock loadingHeight="450px">
            <CustomersSectionContent />
        </LightBlock>
    );
}
